import React, { useCallback, useContext, useRef, useState, useEffect } from 'react';
import ReactCrop from 'react-image-crop';
import UI from '../../ui';
import ModalModule from '../../modal';
import Communication from '../../utils/Communication';
import Util from '../Util';

const { Modal, ModalContext, ModalButtonActionType } = ModalModule;
const { WrappedButton, WrappedButtonType } = UI;
const pixelRatio = 4;

const ErrorCode = {
	FileType: 'fileType',
	ImageTooSmall: 'imageTooSmall',
	ImageTooLarge: 'imageTooLarge',
	NoImage: 'noImage',
	Browser: 'browser',
};

const generateErrorMessage = errorCode => {
	let errorMsg;

	switch (errorCode) {
		case ErrorCode.FileType:
			errorMsg =
				'Invalid image type. Image must be one of the following file types: JPG, JPEG, PNG, GIF, TIF, TIFF, or BMP.';
			break;
		case ErrorCode.ImageTooSmall:
			errorMsg =
				'Image too small. Please use an image that is at least 85px wide and 92px tall.';
			break;
		case ErrorCode.ImageTooLarge:
			errorMsg = 'Image too large.';
			break;
		case ErrorCode.NoImage:
			errorMsg = 'Please select an image to upload.';
			break;
		case ErrorCode.Browser:
			errorMsg = 'Your browser does not support this functionality.';
			break;
		default:
			errorMsg = null;
	}

	return errorMsg;
};

const getResizedCanvas = (canvas, newWidth, newHeight) => {
	const tmpCanvas = document.createElement('canvas');
	tmpCanvas.width = newWidth;
	tmpCanvas.height = newHeight;

	const ctx = tmpCanvas.getContext('2d');
	ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, newWidth, newHeight);

	return tmpCanvas;
};

const generateFinalImage = (previewCanvas, crop, completionCallback) => {
	if (!crop || !previewCanvas) {
		return;
	}

	const canvas = getResizedCanvas(previewCanvas, crop.width, crop.height);
	const data = new FormData();

	canvas.toBlob(
		async blob => {
			data.append('data', blob);
			const response = await Communication.uploadImage(data, 'LabelLogo', {
				'Content-Type': 'multipart/form-data',
			});
			const { imageId, imageUrl } = (response.data && response.data[0]) || {};
			const labelImage = {
				id: imageId.toString(),
				url: imageUrl,
			};

			completionCallback(labelImage, Util.saveNewPreferences);
		},
		'image/png',
		1
	);
};

const UploadImageModal = ({ modalId, data: { labelCustomizationImageId, completionCallback } }) => {
	const [upImg, setUpImg] = useState();
	const [crop, setCrop] = useState({ unit: '%', height: 100, aspect: 1 });
	const [completedCrop, setCompletedCrop] = useState(null);
	const [errorMessage, setErrorMessage] = useState(null);
	const [fileSelectHidden, setFileSelectHidden] = useState(false);
	const { closeModal, showError } = useContext(ModalContext);
	const imgRef = useRef(null);
	const previewCanvasRef = useRef(null);
	const fileInput = useRef(null);

	const onSelectAFileClick = () => {
		fileInput.current.click();
	};

	const createReader = (file, callback) => {
		const reader = new FileReader();

		reader.onload = function(e) {
			const image = new Image();
			image.onload = function() {
				if (callback) {
					callback(this, reader);
				}
			};
			image.src = e.target.result;
		};
		reader.readAsDataURL(file);
	};

	const displayError = errorCode => {
		const errorMsg = generateErrorMessage(errorCode);

		setErrorMessage(errorMsg);
	};

	const onFileSelected = e => {
		if (e.target.files && e.target.files.length > 0) {
			// setSelectedFile(e.target.files[0].name);
			const image = e.target.files[0];

			if (image.type.search(/jpg|jpeg|png|gif|tif|tiff|bmp/) !== -1) {
				// valid image type. clear any error
				displayError();

				createReader(image, function(imageFile, reader) {
					if (imageFile.width < 85 || imageFile.height < 92) {
						// image size too small. display error
						displayError('imageTooSmall');
					} else {
						setUpImg(reader.result);
						setFileSelectHidden(true);
					}
				});
			} else {
				// invalid image type. display error
				displayError('fileType');
			}
		}
	};

	const onImageLoaded = useCallback(img => {
		imgRef.current = img;
	}, []);

	useEffect(() => {
		if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
			return;
		}

		const image = imgRef.current;
		const canvas = previewCanvasRef.current;
		const cCrop = completedCrop;

		const scaleX = image.naturalWidth / image.width;
		const scaleY = image.naturalHeight / image.height;
		const ctx = canvas.getContext('2d');

		canvas.width = cCrop.width * pixelRatio;
		canvas.height = cCrop.height * pixelRatio;

		ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
		ctx.imageSmoothingEnabled = false;

		ctx.drawImage(
			image,
			cCrop.x * scaleX,
			cCrop.y * scaleY,
			cCrop.width * scaleX,
			cCrop.height * scaleY,
			0,
			0,
			cCrop.width,
			cCrop.height
		);
	}, [completedCrop]);

	const onUploadClick = async () => {
		if (labelCustomizationImageId && labelCustomizationImageId !== '0') {
			const response = await Communication.deleteImage(labelCustomizationImageId);

			if (!Communication.isSuccessful(response)) {
				showError({ response });
			}
		}

		generateFinalImage(previewCanvasRef.current, completedCrop, completionCallback);
		closeModal(modalId);
	};

	return (
		<Modal
			modalId={modalId}
			className="common-settings upload-image-modal"
			header="Upload Image"
			size="small"
			primaryButton={{
				text: 'Upload',
				onClick: onUploadClick,
				disabled: !completedCrop?.width || !completedCrop?.height,
				dataqa: 'upload-image-upload-button',
			}}
			secondaryButton={{
				text: 'Cancel',
				type: ModalButtonActionType.Cancel,
				dataqa: 'upload-image-cancel-button',
			}}
		>
			<div className='upload-image-modal-container'>
				<div className="crop-image">
				<ReactCrop
					src={upImg}
					crop={crop}
					className="crop-element"
					onImageLoaded={onImageLoaded}
					onChange={newCrop => setCrop(newCrop)}
					onComplete={newCrop => setCompletedCrop(newCrop)}
				/>
				</div>
				<div className="hidden-canvas">
					<canvas
						ref={previewCanvasRef}
						style={{
							width: (completedCrop && completedCrop.width) || 0,
							height: (completedCrop && completedCrop.height) || 0,
						}}
					/>
				</div>
				{fileSelectHidden ? null : (
					<WrappedButton
						className="select-image-btn"
						label="Browse your computer"
						buttonType={WrappedButtonType.Primary}
						onClick={onSelectAFileClick}
					/>
				)}

				<input type="file" id="file" onChange={onFileSelected} ref={fileInput} />
				{errorMessage && <div className="error-message">{errorMessage}</div>}
			</div>
		</Modal>
	);
};

export default UploadImageModal;
